/**
 * Connection-creation pure helpers.
 *
 * Pure helpers lifted from ConnectionCreationPanel so the auto-create-mode
 * decision and the app-context guard are unit-testable and the panel keeps
 * only rendering/orchestration concerns. Behavior-preserving.
 */
import type { ConnectionParameterSet } from '@/types/connectionParameters';
import { isParameterHidden } from '@/types/connectionParameters';

/**
 * Auto-create mode for connections:
 * - `oauth`: connector only needs OAuth, auto-trigger the OAuth flow
 * - `simple`: connector has no parameters, create immediately
 * - `manual`: connector needs user input, show the full form
 */
export type AutoCreateMode = 'oauth' | 'simple' | 'manual';

/**
 * Detect whether a connector can be auto-created without user input.
 *
 * `displayName` is ignored (handled by the prominent name field). A set with no
 * other parameters is `simple`; a set whose only visible parameters are
 * OAuth/managed-identity is `oauth`; anything else is `manual`.
 */
export function detectAutoCreateMode(
  parameterSet: ConnectionParameterSet | undefined,
): AutoCreateMode {
  if (!parameterSet) return 'manual';

  const params = parameterSet.parameters ?? {};
  const paramEntries = Object.entries(params);

  // Filter out displayName as it's handled by the prominent field
  const nonNameParams = paramEntries.filter(([key]) => key !== 'displayName');

  // No parameters (besides displayName) = could be simple auto-create
  // But we still need to check if it has OAuth
  if (nonNameParams.length === 0) {
    return 'simple';
  }

  // Check if all non-hidden parameters are OAuth
  const visibleNonOAuthParams = nonNameParams.filter(([, param]) => {
    // Skip hidden parameters (uses shared helper — checks hidden, hideInUI)
    if (isParameterHidden(param)) return false;

    // Skip internal-visibility parameters
    if (param.visibility === 'internal') return false;

    // Check if it's an OAuth or managed identity parameter
    const isOAuth = param.type === 'oauthSetting';
    const isMI = param.type === 'managedIdentity';
    return !isOAuth && !isMI;
  });

  // If no visible non-OAuth parameters, we can auto-trigger OAuth
  if (visibleNonOAuthParams.length === 0) {
    // But only if there IS an OAuth parameter
    const hasOAuth = nonNameParams.some(([, param]) => param.type === 'oauthSetting');
    if (hasOAuth) return 'oauth';
  }

  return 'manual';
}

/**
 * Assert that a project and application are selected before a
 * service-provider connection operation. Throws a user-facing error naming the
 * blocked `action` (e.g. "test connection", "create connection") otherwise.
 */
export function requireAppContext(
  project: string | null,
  app: string | null,
  action: string,
): { project: string; app: string } {
  if (!project || !app) {
    throw new Error(
      `Cannot ${action}: no project / application selected. ` +
      'Open an application before creating a service-provider connection.'
    );
  }
  return { project, app };
}
